import type { ChangeProposal, ApplyMode, EditContent, Run, DocAlignment, ChangeFormat, ListEdit, NumStyle, CrossRefEdit, CrossRefTarget } from "@lupp/shared";
import { applyModeTracks, applyModeComments, runsToText } from "@lupp/shared";
import { planGranularReplace, searchSegments, cleanUnicode } from "./wordDiff";

/* global Word */

/**
 * Anwende-Engine für KI-Änderungsvorschläge im Word-Dokument.
 *
 * Fundament: Absätze werden über ihre stabile `uniqueLocalId` (WordApi 1.4) aufgelöst —
 * nicht über den Index (der sich bei jedem Insert/Delete verschiebt). Fällt das Add-in auf
 * Index-Strings zurück (alte Plattform ohne uniqueLocalId), wird auch der Index unterstützt.
 *
 * `replace`/`insert` schreiben den neuen Absatzinhalt in EINER Operation
 * (`insertHtml`/`insertText` mit "Replace") — inklusive Formatierung (aus den `runs` als HTML).
 * Dadurch zeichnet Word pro angewendetem Edit-Block GENAU EINEN Change auf, statt für jedes
 * geänderte Wort und jede Mark eine eigene Revision. Die Formatierung steckt im HTML, es gibt
 * keine separaten Font-Revisionen mehr.
 *
 * Apply-Modi steuern Tracking + Word-Kommentar (raw / with_comment / with_tracked_changes /
 * with_tracked_changes_and_comment).
 */

type Ctx = Word.RequestContext;
type Para = Word.Paragraph;

const isIndexId = (id: string): boolean => /^\d+$/.test(id);

/** Löst einen Absatz über uniqueLocalId (bevorzugt) oder Index auf. Wirft, wenn nicht gefunden. */
async function resolveParagraph(context: Ctx, id: string): Promise<Para> {
  if (isIndexId(id)) {
    const paras = context.document.body.paragraphs;
    paras.load("items");
    await context.sync();
    const n = parseInt(id, 10);
    if (n < 0 || n >= paras.items.length) {
      throw new Error(`paragraph index ${n} out of range (doc has ${paras.items.length} paragraphs)`);
    }
    return paras.items[n];
  }
  const para = context.document.getParagraphByUniqueLocalId(id);
  para.load("uniqueLocalId");
  await context.sync(); // throws ItemNotFound if the paragraph is gone → caller skips this change
  return para;
}

/** Speichert/restauriert den ChangeTracking-Modus um eine Operation herum. */
async function withTracking<T>(context: Ctx, tracks: boolean, fn: () => Promise<T>): Promise<T> {
  context.document.load("changeTrackingMode");
  await context.sync();
  const saved = context.document.changeTrackingMode;
  context.document.changeTrackingMode = tracks ? Word.ChangeTrackingMode.trackAll : Word.ChangeTrackingMode.off;
  await context.sync();
  try {
    return await fn();
  } finally {
    context.document.changeTrackingMode = saved;
    await context.sync();
  }
}

const HTML_ESCAPE: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" };
const escapeHtml = (s: string): string => s.replace(/[&<>"]/g, (c) => HTML_ESCAPE[c]);

/**
 * Bekannte reine Symbol-/Icon-Fonts (MS, Google, Apple, …). Solche Fonts auf lesbaren Text
 * anzuwenden macht ihn unleserlich (Symbol → Kauderwelsch). Wird ein solcher Font in einem Lauf
 * gesetzt, bekommt der übrige (font-lose) Text den Dokument-Standard-Body-Font.
 */
const SYMBOL_FONTS = new Set<string>([
  "wingdings", "wingdings 2", "wingdings 3", "webdings", "symbol", "marlett", // Microsoft
  "segoe mdl2 assets", "segoe fluent icons", "segoe ui symbol", "segoe ui emoji",
  "material icons", "material icons outlined", "material icons round", "material icons sharp", "material icons two tone",
  "material symbols outlined", "material symbols rounded", "material symbols sharp", // Google
  "noto emoji", "noto color emoji",
  "apple symbols", "apple color emoji", // Apple
  "font awesome", "fontawesome", // gängige Icon-Fonts
]);
const isSymbolFont = (name: string | undefined): boolean => !!name && SYMBOL_FONTS.has(name.trim().toLowerCase());

/** Inline-HTML für einen einzelnen Lauf (Text + Marks, oder Umbruch/Tab/Feld). */
function runToHtml(run: Run, defaultFont?: string): string {
  if (run.br) return "<br/>";
  if (run.tab) return "\t"; // unter white-space:pre-wrap als echter Tab gerendert
  const raw = run.t ?? run.field?.text ?? "";
  if (!raw) return "";
  let inner = escapeHtml(cleanUnicode(raw));

  if (run.b) inner = `<strong>${inner}</strong>`;
  if (run.i) inner = `<em>${inner}</em>`;
  if (run.u) inner = `<u>${inner}</u>`;
  if (run.strike) inner = `<s>${inner}</s>`;
  if (run.sup) inner = `<sup>${inner}</sup>`;
  if (run.sub) inner = `<sub>${inner}</sub>`;

  const styles: string[] = [];
  if (run.color) styles.push(`color:#${run.color.replace(/^#/, "")}`);
  if (run.bg) styles.push(`background-color:#${run.bg.replace(/^#/, "")}`);
  if (run.font) styles.push(`font-family:'${run.font.replace(/'/g, "")}'`);
  else if (defaultFont) styles.push(`font-family:'${defaultFont.replace(/'/g, "")}'`); // Guard: lesbarer Body-Font
  if (run.size) styles.push(`font-size:${run.size}pt`);
  if (styles.length) inner = `<span style="${styles.join(";")}">${inner}</span>`;

  if (run.link) inner = `<a href="${escapeHtml(run.link)}">${inner}</a>`;
  return inner;
}

/**
 * Baut Inline-HTML aus den Läufen. `white-space:pre-wrap` bewahrt Mehrfach-Leerzeichen und
 * Tabs. Es werden bewusst KEINE Block-Elemente (<p>) erzeugt — der Inhalt ersetzt nur den
 * Inhalt EINES bestehenden Absatzes, dessen Absatzmarke erhalten bleibt.
 * `defaultFont` wird font-losen Text-Läufen aufgeprägt (Symbol-Font-Guard, s. writeContent).
 */
function runsToHtml(runs: Run[], defaultFont?: string): string {
  const body = runs.map((r) => runToHtml(r, defaultFont)).join("");
  return `<span style="white-space:pre-wrap">${body}</span>`;
}

/**
 * Schreibt den neuen Absatzinhalt in EINER Operation in den Content-Range:
 * mit Formatierung als HTML (aus den Läufen), sonst als Plain-Text. Unter TrackAll ergibt das
 * pro Edit-Block genau einen Change (eine Löschung des alten + eine Einfügung des neuen Inhalts).
 *
 * Symbol-Font-Guard: Setzt ein Lauf einen bekannten reinen Symbol-Font (Wingdings, Symbol, …),
 * bekommt der übrige (font-lose) Text explizit den Dokument-Standard-Body-Font — so kann ein
 * Symbol-Font nicht auf lesbaren Text durchschlagen. Läufe mit eigenem Font bleiben unberührt.
 */
/** Body-Standard-Font, aber nur wenn ein Symbol-Font im Spiel ist (Guard, s. runToHtml). Sonst undefined. */
async function bodyDefaultFontIfSymbol(context: Ctx, content: EditContent | undefined): Promise<string | undefined> {
  if (!content?.runs?.length) return undefined;
  const symbolInPlay = content.runs.some((r) => isSymbolFont(r.font)) || isSymbolFont(content.marker?.font);
  if (!symbolInPlay) return undefined;
  const bodyFont = context.document.body.font;
  bodyFont.load("name");
  await context.sync();
  return bodyFont.name || undefined;
}

async function writeContent(context: Ctx, range: Word.Range, content: EditContent | undefined, fallbackText: string): Promise<void> {
  if (content?.runs?.length) {
    const defaultFont = await bodyDefaultFontIfSymbol(context, content);
    range.insertHtml(runsToHtml(content.runs, defaultFont), "Replace");
  } else {
    range.insertText(cleanUnicode(fallbackText), "Replace");
  }
  await context.sync();
}

/**
 * Granulares Ersetzen: difft Ist- gegen Ziel-Text (Wort-Level) und ersetzt pro Änderungsblock
 * die Spanne ZWISCHEN den beiden umgebenden unveränderten Wörtern (Anker) in EINER Operation —
 * inklusive des Trenn-Whitespace. Dadurch verklebt nichts und es entstehen keine doppelten oder
 * fehlenden Leerzeichen; pro Block ein Tracked Change, unveränderte Wörter bleiben unberührt.
 *
 * Es wird bewusst `insertText` (NICHT `insertHtml`) genutzt: insertText trimmt keinen Whitespace,
 * insertHtml schneidet hingegen führende Leerzeichen des Fragments ab (→ verklebte Wörter). Die
 * Formatierung der neuen Wörter setzt anschließend `reconcileMarks` (über die `runs`).
 * Die Anker werden im nach-vorn-wandernden `tail` getroffen (= das jeweils nächste Vorkommen).
 */
async function diffReplaceContent(context: Ctx, para: Para, newText: string): Promise<void> {
  const content = para.getRange("Content");
  content.load("text");
  await context.sync();

  const oldText = cleanUnicode(content.text ?? "");
  const target = cleanUnicode(newText);
  if (oldText === target) return; // kein Text-Diff (Marks/Attrs werden separat behandelt)

  const ops = planGranularReplace(oldText, target);
  let tail = content; // verbleibender Bereich; wandert mit dem linken Anker nach vorn

  for (const op of ops) {
    try {
      // Linker Anker: das letzte unveränderte Wort vor dem Block (bzw. Absatzanfang).
      let leftEnd: Word.Range;
      if (op.prevAnchor == null) {
        leftEnd = content.getRange("Start");
      } else {
        const pw = tail.search(op.prevAnchor, { matchCase: true }).getFirst();
        await context.sync();
        leftEnd = pw.getRange("End");
        tail = pw.getRange("After").expandTo(para.getRange("End"));
      }

      // Rechter Anker: das erste unveränderte Wort nach dem Block (bzw. Absatzende).
      let rightStart: Word.Range;
      if (op.nextAnchor == null) {
        rightStart = content.getRange("End");
      } else {
        const nw = tail.search(op.nextAnchor, { matchCase: true }).getFirst();
        await context.sync();
        rightStart = nw.getRange("Start");
      }

      // Plain-Text-Ersetzung der Zwischen-Spanne (Leerzeichen bleiben exakt erhalten).
      leftEnd.expandTo(rightStart).insertText(target.slice(op.newStart, op.newEnd), "Replace");
      await context.sync();
    } catch {
      // Anker nicht eindeutig auffindbar (Sonderzeichen / bestehende Changes) → Block überspringen.
    }
  }
}

/** Hat dieser Lauf eine Inline-Auszeichnung? */
function runHasMarks(run: Run): boolean {
  return Boolean(run.b || run.i || run.strike || run.sup || run.sub || run.u || run.color || run.bg || run.size || run.font);
}

/** Setzt die Marks eines Laufs auf einen geladenen Range — NUR wo abweichend (→ minimale w:rPrChange). */
function markDiffsToFont(font: Word.Font, run: Run): void {
  if (!!run.b !== font.bold) font.bold = !!run.b;
  if (!!run.i !== font.italic) font.italic = !!run.i;
  if (!!run.strike !== font.strikeThrough) font.strikeThrough = !!run.strike;
  if (!!run.sup !== font.superscript) font.superscript = !!run.sup;
  if (!!run.sub !== font.subscript) font.subscript = !!run.sub;
  const wantU = run.u ? (run.u === "double" ? Word.UnderlineType.double : Word.UnderlineType.single) : Word.UnderlineType.none;
  if (wantU !== font.underline) font.underline = wantU;
  if (run.color) {
    const w = `#${run.color.replace(/^#/, "")}`;
    if (w !== font.color) font.color = w;
  }
  if (run.bg) {
    const w = `#${run.bg.replace(/^#/, "")}`;
    if (w !== font.highlightColor) font.highlightColor = w;
  }
  if (typeof run.size === "number" && run.size !== font.size) font.size = run.size;
  if (run.font && !isSymbolFont(run.font) && run.font !== font.name) font.name = run.font;
}

/**
 * Gleicht die Marks der formatierten Läufe mit dem Ist-Zustand ab und setzt nur Abweichungen —
 * so wird eine reine Formatierungsänderung auf unverändertem Text (z. B. „dieses Wort fett")
 * als enger w:rPrChange getrackt, und bereits korrekt eingefügter (ADDED-)Text bleibt ein No-Op.
 * Begrenzt auf bis zum letzten markierten Lauf; Läufe ohne Marks dienen nur dem Weiterschieben.
 * (Formatierung ENTFERNEN über Weglassen ist hier nicht möglich — dafür replace_full nutzen.)
 */
async function reconcileMarks(context: Ctx, para: Para, runs: Run[]): Promise<void> {
  let lastMarked = -1;
  runs.forEach((r, i) => {
    if (r.t && runHasMarks(r)) lastMarked = i;
  });
  if (lastMarked < 0) return;

  const pending: { font: Word.Font; run: Run }[] = [];
  let tail = para.getRange("Content");
  for (let i = 0; i <= lastMarked; i++) {
    const run = runs[i];
    if (!run.t) continue;
    const marked = runHasMarks(run);
    for (const seg of searchSegments(run.t)) {
      try {
        const found = tail.search(seg, { matchCase: true }).getFirst();
        if (marked) found.font.load("bold,italic,underline,strikeThrough,superscript,subscript,color,highlightColor,size,name");
        await context.sync();
        if (marked) pending.push({ font: found.font, run });
        tail = found.getRange("After").expandTo(para.getRange("End"));
      } catch {
        // nicht auffindbar → überspringen
      }
    }
  }
  for (const { font, run } of pending) markDiffsToFont(font, run);
  await context.sync();
}

/** Maps OOXML field kind to the Word.FieldType string accepted by Range.insertField (WordApi 1.5). */
function kindToFieldType(kind: string): string {
  const map: Record<string, string> = {
    REF: "Ref",
    PAGE: "Page",
    DATE: "Date",
    TIME: "Time",
    PAGEREF: "PageRef",
    NUMREF: "NumRef",
    NUMCHARS: "NumChars",
    NUMWORDS: "NumWords",
    NUMPAGES: "NumPages",
    SEQ: "Seq",
    STYLEREF: "StyleRef",
    HYPERLINK: "Hyperlink",
    TOC: "Toc",
    TC: "Tc",
  };
  return map[kind.toUpperCase()] ?? "Other";
}

/** Bookmark-referenzierende Feldarten — fehlt das Ziel-Bookmark, zeigt Word „Verweisquelle nicht gefunden". */
const BOOKMARK_REF_KINDS = new Set(["REF", "PAGEREF", "NUMREF"]);

/** Bookmark-Name aus einem Feldcode (erstes Nicht-Switch-Token nach dem Feldnamen), sonst null. */
function fieldBookmarkName(kind: string, code: string): string | null {
  if (!BOOKMARK_REF_KINDS.has(kind.toUpperCase())) return null;
  const toks = code.trim().split(/\s+/);
  let i = toks[0] && toks[0].toUpperCase() === kind.toUpperCase() ? 1 : 0; // Feldnamen überspringen
  for (; i < toks.length; i++) {
    if (!toks[i].startsWith("\\")) return toks[i].replace(/^"|"$/g, "");
  }
  return null;
}

/**
 * Normalisiert einen OOXML-Feldcode für `Range.insertField(type, text)`: `text` darf NUR die
 * Argumente (Bookmark + Switches) enthalten — das Feld-Keyword (REF/PAGEREF/…) und `\* MERGEFORMAT`
 * ergänzt Word selbst. Ungestript entstünde sonst „REF REF … \* MERGEFORMAT \* MERGEFORMAT".
 */
function normalizeFieldCode(kind: string, code: string): string {
  let s = code.trim();
  s = s.replace(new RegExp(`^(?:${kind.toUpperCase()}\\s+)+`, "i"), ""); // führendes Keyword (auch doppelt)
  s = s.replace(/\\\*\s*MERGEFORMAT/gi, ""); // Word hängt MERGEFORMAT selbst an
  return s.replace(/\s+/g, " ").trim();
}

/** Namen aller Bookmarks im Dokumentkörper; null, wenn die API fehlt (dann nicht prüfbar → Feld trotzdem einfügen). */
async function docBookmarkNames(context: Ctx): Promise<Set<string> | null> {
  const body = context.document.body as unknown as Record<string, unknown>;
  if (typeof body.getRange !== "function") return null;
  const bodyRange = (body.getRange as () => unknown).call(context.document.body) as Record<string, unknown>;
  if (typeof bodyRange.getBookmarks !== "function") return null;
  // getBookmarks → ClientResult<string[]>: nach sync() liegt das Namensarray in `.value`.
  const result = (bodyRange.getBookmarks as (h: boolean, a: boolean) => { value: string[] }).call(bodyRange, true, true);
  await context.sync();
  return new Set(result.value || []);
}

/**
 * Nach dem HTML-Write Feld-Runs als echte Word-Felder setzen (WordApi 1.5+).
 * Fallback bei fehlender insertField-API: Text bleibt, Hinweis-Kommentar wird eingefügt.
 * Läufe ohne code (kein Feldcode bekannt) werden übersprungen.
 *
 * Totes Verweisziel: Zeigt ein REF/PAGEREF/NUMREF auf ein Bookmark, das im Dokument nicht (mehr)
 * existiert, wird das Feld NICHT eingefügt — der gecachte Anzeigetext bleibt stehen, statt dass Word
 * „Verweisquelle nicht gefunden" anzeigt (häufig bei bereits im Quelldokument kaputten Verweisen).
 */
async function applyFieldRuns(context: Ctx, para: Para, runs: Run[]): Promise<void> {
  const fieldRuns = runs.filter((r) => r.field?.code && r.field.text);
  if (!fieldRuns.length) return;

  let docBookmarks: Set<string> | null | undefined; // lazy, einmal pro Aufruf geladen

  for (const run of fieldRuns) {
    const { kind, text, code } = run.field!;
    if (!text || !code) continue;

    const bm = fieldBookmarkName(kind, code);
    if (bm) {
      if (docBookmarks === undefined) docBookmarks = await docBookmarkNames(context);
      if (docBookmarks && !docBookmarks.has(bm)) continue; // Ziel fehlt → gecachten Text belassen
    }

    try {
      const found = para.getRange("Content").search(text, { matchCase: true }).getFirst();
      await context.sync();
      const insertField = (found as unknown as Record<string, unknown>).insertField;
      if (typeof insertField !== "function") {
        // WordApi < 1.5: leave display text, add manual-check comment
        found.insertComment("Verweis bitte manuell prüfen");
        await context.sync();
        return;
      }
      (insertField as (loc: string, type: string, code: string) => void).call(found, "Replace", kindToFieldType(kind), normalizeFieldCode(kind, code));
      await context.sync();
    } catch {
      // field text not uniquely findable — skip
    }
  }
}

// WICHTIG: keine `Word.*`-WERTE auf Modul-Top-Level — `Word` existiert erst nach Office.onReady,
// sonst „ReferenceError: Word is not defined" beim Bundle-Load (Race). Daher die Enum-String-
// Werte literal (Typ Word.Alignment ist reine Compile-Time-Annotation und wird gelöscht).
const ALIGNMENT_MAP: Record<DocAlignment, Word.Alignment> = {
  left: "Left" as Word.Alignment,
  center: "Centered" as Word.Alignment,
  right: "Right" as Word.Alignment,
  justify: "Justified" as Word.Alignment,
};

/**
 * Setzt die Marker-/Absatzmarken-FARBE. Office.js exponiert die Absatzmarke nicht isoliert, also
 * wird die Farbe auf den GANZEN Absatz (inkl. Marke) gesetzt — der Marker erbt sie über die
 * Absatzmarke. Bewusst NUR die Farbe: `font`/`size`/`bold`/… des Markers stammen von der
 * Listenebene (z. B. `Symbol`/10) und würden, auf den Gesamtbereich angewendet, den sichtbaren
 * TEXT zerstören (Symbol-Font → Kauderwelsch). `color: "auto"` setzt die Farbe zurück.
 */
async function applyMarker(context: Ctx, para: Para, marker: Run): Promise<void> {
  if (marker.color === undefined) return;
  const c = marker.color.toLowerCase();
  // Office.js Font.color akzeptiert kein "" / "auto" (wirft). "automatisch" ist nicht sauber
  // exponiert; pragmatisch auf Schwarz — entspricht dem Default-Text und entfernt ein rotes Bullet.
  const want = c === "auto" || c === "none" || c === "" ? "#000000" : `#${marker.color.replace(/^#/, "")}`;

  // Nur die Absatzmarke/das Aufzählungszeichen soll die Farbe bekommen — NICHT der sichtbare Text.
  // Office.js kann die Absatzmarke nicht isoliert ansprechen, daher: aktuelle Textfarbe merken,
  // den GANZEN Absatz (inkl. Marke) einfärben, dann die Textfarbe auf dem Content-Range
  // wiederherstellen → nur die Marke behält die neue Farbe. (Keine KI-Kooperation nötig.)
  const content = para.getRange("Content");
  content.font.load("color");
  const bodyFont = context.document.body.font;
  bodyFont.load("color");
  await context.sync();
  // Ist die Textfarbe nicht explizit gesetzt (automatisch ⇒ ""), nimm die Body-Standardfarbe,
  // sonst Schwarz — damit der Text NIE in der Marker-Farbe hängen bleibt.
  const prevTextColor = content.font.color || bodyFont.color || "#000000";

  para.getRange("Whole").font.color = want;
  await context.sync();

  content.font.color = prevTextColor; // immer wiederherstellen
  await context.sync();
}

const NUM_STYLE_MAP: Record<Exclude<NumStyle, "bullet">, Word.ListNumbering> = {
  arabic: Word.ListNumbering.arabic,
  upperRoman: Word.ListNumbering.upperRoman,
  lowerRoman: Word.ListNumbering.lowerRoman,
  upperLetter: Word.ListNumbering.upperLetter,
  lowerLetter: Word.ListNumbering.lowerLetter,
};

/**
 * Wendet eine Listen-Operation an:
 * none     → Absatz aus Liste lösen (detachFromList).
 * continue → Absatz in die Liste des Referenzabsatzes einreihen (gleiche numId → Nummerierung korrekt).
 *            Ist der Referenzabsatz nicht in einer Liste → no-op.
 * level    → Ebene innerhalb der aktuellen Liste ändern.
 *            Ist der Absatz nicht in einer Liste → no-op.
 * new      → neue Liste anlegen: bullet → setLevelBullet; nummeriert → setLevelNumbering.
 *            Nur verwenden, wenn kein passender Listenstil existiert (sonst: continue).
 *
 * NOTE: Office.js zeichnet Listenänderungen ggf. nicht immer als Tracked Change auf (Word-Limit) —
 * das ist ein bekanntes Word-Constraint, kein Bug dieser Implementierung.
 */
async function applyList(context: Ctx, para: Para, list: ListEdit): Promise<void> {
  switch (list.op) {
    case "none":
      para.detachFromList();
      await context.sync();
      break;
    case "continue": {
      const ref = context.document.getParagraphByUniqueLocalId(list.ref_paragraph_id);
      ref.listOrNullObject.load("isNullObject,id");
      ref.listItem.load("level");
      await context.sync();
      if (ref.listOrNullObject.isNullObject) return;
      para.attachToList(ref.listOrNullObject.id, list.level ?? ref.listItem.level);
      await context.sync();
      break;
    }
    case "level": {
      para.listOrNullObject.load("isNullObject,id");
      await context.sync();
      if (para.listOrNullObject.isNullObject) return;
      para.attachToList(para.listOrNullObject.id, list.level);
      await context.sync();
      break;
    }
    case "new": {
      const level = list.level ?? 0;
      const newList = para.startNewList();
      if (list.numStyle === "bullet") {
        newList.setLevelBullet(level, Word.ListBullet.solid);
      } else {
        newList.setLevelNumbering(level, NUM_STYLE_MAP[list.numStyle]);
      }
      await context.sync();
      break;
    }
  }
}

/**
 * Bereichs-Promote/Demote: verschiebt die Listenebene aller Absätze relativ um `delta`
 * (negativ = promote, positiv = demote). Ebene wird bei 0 geclampt. Absätze außerhalb
 * einer Liste werden übersprungen.
 */
export async function promoteDemoteRange(paragraphIds: string[], delta: number): Promise<void> {
  if (!paragraphIds.length || delta === 0) return;
  await Word.run(async (context) => {
    const paras = paragraphIds.map((id) => {
      const p = context.document.getParagraphByUniqueLocalId(id);
      p.listOrNullObject.load("isNullObject,id");
      p.listItem.load("level");
      return p;
    });
    await context.sync();
    for (const p of paras) {
      if (p.listOrNullObject.isNullObject) continue;
      const newLevel = Math.max(0, p.listItem.level + delta);
      p.attachToList(p.listOrNullObject.id, newLevel);
    }
    await context.sync();
  });
}

/**
 * Listenaufteilung (Split): löst den Absatz `splitAtId` aus seiner aktuellen Liste und
 * startet dort eine neue. Optionale `continuationIds` werden an die neue Liste angehängt
 * (ihre bisherige Ebene bleibt erhalten). Ist `splitAtId` noch nicht in einer Liste,
 * entfällt das Detach.
 */
export async function splitList(splitAtId: string, numStyle: NumStyle, continuationIds: string[] = []): Promise<void> {
  await Word.run(async (context) => {
    const splitPara = context.document.getParagraphByUniqueLocalId(splitAtId);
    splitPara.listOrNullObject.load("isNullObject,id");
    splitPara.listItem.load("level");

    const contParas = continuationIds.map((id) => {
      const p = context.document.getParagraphByUniqueLocalId(id);
      p.listItem.load("level");
      return p;
    });

    await context.sync();

    const splitLevel = splitPara.listOrNullObject.isNullObject ? 0 : splitPara.listItem.level;
    const contLevels = contParas.map((p) => p.listItem.level);

    if (!splitPara.listOrNullObject.isNullObject) {
      splitPara.detachFromList();
      await context.sync();
    }

    const newList = splitPara.startNewList();
    if (numStyle === "bullet") {
      newList.setLevelBullet(splitLevel, Word.ListBullet.solid);
    } else {
      newList.setLevelNumbering(splitLevel, NUM_STYLE_MAP[numStyle]);
    }
    await context.sync();

    if (contParas.length) {
      newList.load("id");
      await context.sync();
      contParas.forEach((p, i) => {
        p.attachToList(newList.id, contLevels[i]);
      });
      await context.sync();
    }
  });
}

/**
 * Listenzusammenführung (Merge): hängt alle Absätze in `paragraphIds` an die Liste des
 * Anker-Absatzes `anchorId` an. Jeder Absatz behält seine bisherige Ebene. Ist `anchorId`
 * nicht in einer Liste, ist dies ein No-op. Unterstützt gemischte numId-Quellen: die
 * Herkunfts-Liste der zusammenzuführenden Absätze spielt keine Rolle.
 */
export async function mergeList(paragraphIds: string[], anchorId: string): Promise<void> {
  if (!paragraphIds.length) return;
  await Word.run(async (context) => {
    const anchor = context.document.getParagraphByUniqueLocalId(anchorId);
    anchor.listOrNullObject.load("isNullObject,id");

    const paras = paragraphIds.map((id) => {
      const p = context.document.getParagraphByUniqueLocalId(id);
      p.listOrNullObject.load("isNullObject");
      p.listItem.load("level");
      return p;
    });

    await context.sync();

    if (anchor.listOrNullObject.isNullObject) return;

    const listId = anchor.listOrNullObject.id;
    paras.forEach((p, i) => {
      const level = p.listOrNullObject.isNullObject ? 0 : paras[i].listItem.level;
      p.attachToList(listId, level);
    });
    await context.sync();
  });
}

/** Heading-Level aus einem Style-Namen — tolerant ggü. fehlerhaften Namen wie "Überschrift #1". */
const HEADING_NAME_RE = /(?:heading|überschrift|berschrift)\s*#?\s*([1-6])/i;
function headingLevelFromName(name?: string): number | undefined {
  const m = name?.match(HEADING_NAME_RE);
  return m ? parseInt(m[1], 10) : undefined;
}

/**
 * Setzt Style/Heading FEHLERTOLERANT. Ein ungültiger/fehlender Style-Name (das Modell erfindet z. B.
 * "Überschrift #1" oder schickt "Body Text", das es im Dokument nicht gibt) wirft beim sync()
 * `InvalidArgument` und würde sonst den GANZEN Absatz-Apply abbrechen. Daher: bevorzugt ein Heading
 * über `styleBuiltIn` (locale-unabhängig; Level aus content.level ODER aus dem Namen ableiten),
 * sonst der Style-Name in eigenem try/catch — schlägt er fehl, wird er übersprungen, der Text bleibt.
 */
async function applyParagraphStyle(context: Ctx, para: Para, content: EditContent): Promise<number | undefined> {
  const level =
    (content.level && content.level >= 1 && content.level <= 6 ? content.level : undefined) ??
    headingLevelFromName(content.style);
  // Bevorzugt den EXPLIZITEN Style-Namen (die echte Dokument-Vorlage — z. B. die Abschnitts-Vorlage,
  // die "3." erzeugt). Der eingebaute HeadingN ist nur Fallback bei ungültigem/fehlendem Namen: in
  // Dokumenten mit eigenen Abschnitts-Styles liefert er sonst die FALSCHE Nummerierung (z. B. "A.").
  if (content.style) {
    try {
      para.style = content.style;
      await context.sync();
      return level;
    } catch (err) {
      console.warn(`[applyParagraphStyle] style "${content.style}" not applied (invalid/missing):`, err);
    }
  }
  if (level) {
    try {
      para.styleBuiltIn = `Heading${level}` as Word.BuiltInStyleName;
      await context.sync();
    } catch (err) {
      console.warn(`[applyParagraphStyle] styleBuiltIn Heading${level} failed:`, err);
    }
  }
  return level;
}

/** Setzt die Absatz-Attribute (Style/Ausrichtung/Einzug/Abstände) — nur wo angegeben & abweichend. */
async function applyParagraphAttrs(context: Ctx, para: Para, content: EditContent): Promise<void> {
  para.load("alignment,leftIndent,firstLineIndent,spaceBefore,spaceAfter");
  await context.sync();

  const headingLevel = await applyParagraphStyle(context, para, content);

  if (content.align && para.alignment !== ALIGNMENT_MAP[content.align]) {
    para.alignment = ALIGNMENT_MAP[content.align];
  }
  // EINHEITEN: Modell/get_document führen Einzug & Abstand in OOXML-TWIPS (1 pt = 20 Twips), Office.js
  // erwartet aber PUNKTE. Ohne Umrechnung wird alles 20× zu groß (z. B. indent 713 → 713pt ≈ 9,9" →
  // Text quetscht sich in eine hauchdünne Spalte über mehrere „Leerseiten"; spacing 160 → 160pt ≈ 2,2").
  const toPt = (twips: number): number => twips / 20;

  // Wird der Absatz einer Liste zugeordnet (continue/level/new), bestimmt die LISTENEBENE den Einzug —
  // ein zusätzlicher manueller leftIndent würde sich addieren. Daher Einzug dann überspringen.
  const listGovernsIndent = !!content.list && content.list.op !== "none";
  if (content.indent && !listGovernsIndent) {
    if (typeof content.indent.left === "number") para.leftIndent = toPt(content.indent.left);
    if (typeof content.indent.firstLine === "number") para.firstLineIndent = toPt(content.indent.firstLine);
  }
  if (content.spacing) {
    if (typeof content.spacing.before === "number") para.spaceBefore = toPt(content.spacing.before);
    if (typeof content.spacing.after === "number") para.spaceAfter = toPt(content.spacing.after);
  }
  await context.sync();
  // List-Zuordnung. Bei einem HEADING-Absatz NICHT explizit attachen: Die Nummerierung kommt aus dem
  // Heading-Style (style-vererbt), und ein attachToList auf einem frisch eingefügten Heading wirft
  // GeneralException und würde mit der Style-Nummerierung kollidieren. Sonst fehlertolerant attachen.
  if (content.list && !headingLevel) {
    try {
      await applyList(context, para, content.list);
    } catch (err) {
      console.warn("[applyParagraphAttrs] applyList failed (skipped):", err);
    }
  }
}

/** Legacy-`format`-Feld (ChangeFormat) anwenden: Absatz-Stil/Ausrichtung + Zeichen-Format auf den Text. */
async function applyLegacyFormat(context: Ctx, para: Para, fmt: ChangeFormat): Promise<void> {
  if (fmt.styleName) para.style = fmt.styleName;
  if (fmt.alignment) para.alignment = ALIGNMENT_MAP[fmt.alignment];
  const f = para.getRange("Content").font;
  if (fmt.bold !== undefined) f.bold = fmt.bold;
  if (fmt.italic !== undefined) f.italic = fmt.italic;
  if (fmt.underline !== undefined) f.underline = fmt.underline ? Word.UnderlineType.single : Word.UnderlineType.none;
  if (fmt.strike !== undefined) f.strikeThrough = fmt.strike;
  if (typeof fmt.size === "number") f.size = fmt.size;
  if (fmt.color) f.color = `#${fmt.color.replace(/^#/, "")}`;
  if (fmt.highlight) f.highlightColor = `#${fmt.highlight.replace(/^#/, "")}`;
  await context.sync();
}

/** Generates a collision-resistant Word bookmark name from a paragraph's uniqueLocalId. */
function generateBookmarkName(paragraphId: string): string {
  const clean = paragraphId.replace(/[^a-zA-Z0-9]/g, "");
  return `_Ref${clean.slice(0, 8)}`;
}

/**
 * Neuen Querverweis erstellen: Bookmark am Ziel-Absatz + REF-Feld am Quell-Absatz.
 *
 * Capability-Gating: fehlt Range.insertBookmark (WordApi < 1.4) → Hinweis-Kommentar am Ziel;
 * fehlt Range.insertField (WordApi < 1.5) → REF-Feld wird nicht eingefügt (Bookmark bleibt).
 *
 * Bookmark-Reihenfolge:
 *   1. cross_ref.bookmark_name → immer neu einsetzen (expliziter Name).
 *   2. Vorhandenes _Ref*-Bookmark am Ziel → wiederverwenden.
 *   3. Keines gefunden → generierten Namen einsetzen.
 */
/**
 * Stellt am Zielabsatz ein Bookmark sicher und liefert dessen Namen.
 * Reihenfolge: expliziter Name (immer neu) → vorhandenes `_Ref*` wiederverwenden → generierter Name.
 * `null`, wenn `insertBookmark` fehlt (WordApi < 1.4) — Caller setzt Hinweis-Kommentar.
 */
async function resolveTargetBookmark(context: Ctx, targetParagraphId: string, explicitName?: string): Promise<string | null> {
  const targetPara = context.document.getParagraphByUniqueLocalId(targetParagraphId);
  targetPara.load("uniqueLocalId");
  await context.sync();
  const targetRange = targetPara.getRange("Content");
  const rawTarget = targetRange as unknown as Record<string, unknown>;

  if (typeof rawTarget.insertBookmark !== "function") return null;

  if (explicitName) {
    (rawTarget.insertBookmark as (n: string) => void).call(targetRange, explicitName);
    await context.sync();
    return explicitName;
  }
  if (typeof rawTarget.getBookmarks === "function") {
    const result = (rawTarget.getBookmarks as (h: boolean, a: boolean) => { value: string[] }).call(targetRange, true, true);
    await context.sync();
    const existing = (result.value || []).find((n) => n.startsWith("_Ref"));
    if (existing) return existing;
  }
  const name = generateBookmarkName(targetParagraphId);
  (rawTarget.insertBookmark as (n: string) => void).call(targetRange, name);
  await context.sync();
  return name;
}

/**
 * Querverweis(e) anlegen. Default `ref_kind: "number"` → `\w \h` (Nummer im vollen Kontext, zieht bei
 * Umnummerierung nach); `"text"` → `\h`. Kein fest getippter Text — immer auto-aktualisierende Felder.
 *
 * Zusammengesetzte Verweise (`targets`): Nummer und Unterpunkt stammen aus verschiedenen Nummernströmen
 * (eigene numId/Bookmark) → "2.1(a)" wird als ZWEI verkettete REF-Felder `[REF 2.1][REF (a)]` gesetzt
 * (Trenner `separator`, Default ""). Jedes Feld aktualisiert sich unabhängig.
 */
async function applyCrossRef(context: Ctx, sourcePara: Para, crossRef: CrossRefEdit): Promise<void> {
  const targets: CrossRefTarget[] =
    crossRef.targets && crossRef.targets.length
      ? crossRef.targets
      : crossRef.target_paragraph_id
        ? [{ target_paragraph_id: crossRef.target_paragraph_id, bookmark_name: crossRef.bookmark_name, ref_kind: crossRef.ref_kind }]
        : [];
  if (!targets.length) return;

  // Pro Ziel ein Bookmark sichern und den Feldcode bauen.
  // Nummern-Switches: das ERSTE Feld zeigt den vollen Kontext (`\w` → "2.2"); FOLGE-Felder eines
  // zusammengesetzten Verweises nur die Eigennummer ohne Kontext (`\n` → "(b)"), sonst würde der
  // Unterpunkt seinen eigenen Vollkontext doppeln ("2.2" + "1.2.1(b)" statt "2.2(b)").
  const fieldCodes: string[] = [];
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i];
    const bm = await resolveTargetBookmark(context, t.target_paragraph_id, t.bookmark_name);
    if (bm === null) {
      sourcePara.getRange("Content").insertComment("Querverweis bitte manuell anlegen (Bookmark + REF-Feld)");
      await context.sync();
      return;
    }
    // Nummern-Verweis: erstes/einzelnes Feld `\r \h` (relative Kontextnummer wie "2.2"/"5.1" — das
    // nutzen die bestehenden Verweise im Dokument zuverlässig; `\w` scheitert bei style-nummerierten
    // Klauseln mit "Verweisquelle nicht gefunden"). Folge-Felder `\n \h` (nur Eigennummer, z. B. "(b)").
    const switches = t.ref_kind === "text" ? "\\h" : i === 0 ? "\\r \\h" : "\\n \\h";
    fieldCodes.push(`${bm} ${switches}`);
  }

  const separator = crossRef.separator ?? "";
  const sourceRange = sourcePara.getRange("Content");
  const rawSource = sourceRange as unknown as Record<string, unknown>;

  // Einfügeanker: markierten Text ersetzen, sonst am Absatzende anhängen.
  let anchor: Record<string, unknown> = rawSource;
  let firstLoc = "End";
  if (crossRef.display_text) {
    try {
      const found = sourceRange.search(crossRef.display_text, { matchCase: false }).getFirst();
      await context.sync();
      const rawFound = found as unknown as Record<string, unknown>;
      if (typeof rawFound.insertField === "function") {
        anchor = rawFound;
        firstLoc = "Replace";
      }
    } catch {
      // Suche fehlgeschlagen → am Ende einfügen
    }
  }
  if (typeof anchor.insertField !== "function") return;

  // Erstes Feld am Anker; weitere Felder (+ Trenner) hinter dem jeweils vorigen Feld verketten.
  type FieldLike = {
    result: { insertText: (t: string, l: string) => void; insertField: (l: string, t: string, c: string) => FieldLike };
    updateResult?: () => void;
  };
  const inserted: FieldLike[] = [];
  let prev = (anchor.insertField as (l: string, t: string, c: string) => FieldLike).call(anchor, firstLoc, "Ref", fieldCodes[0]);
  inserted.push(prev);
  await context.sync();
  for (let i = 1; i < fieldCodes.length; i++) {
    if (separator) prev.result.insertText(separator, "End");
    prev = prev.result.insertField("After", "Ref", fieldCodes[i]);
    inserted.push(prev);
    await context.sync();
  }

  // Neu eingefügte Felder direkt auswerten lassen, damit die Nummer (z. B. "2.2(b)") sofort steht —
  // ohne dass der Nutzer F9 drücken muss. Das frische Ziel-Bookmark wird so zuverlässig aufgelöst.
  let updated = false;
  for (const f of inserted) {
    if (f && typeof f.updateResult === "function") {
      f.updateResult();
      updated = true;
    }
  }
  if (updated) await context.sync();
}

/**
 * Aktualisiert ALLE Felder im Dokument-Body (Querverweise, Seitenverweise, TOC, Datum …), damit
 * Verweisnummern nach jeder Änderung sofort die aktuelle Nummerierung zeigen — ohne dass der Nutzer
 * F9 drücken muss. Office.js hat keine Sammel-Update-Methode → es wird über die Felder iteriert
 * (gebündelt, ein Roundtrip). Best-effort: fehlt die Field-API, passiert nichts; ein Fehler hier darf
 * den Apply NIE scheitern lassen.
 */
async function updateAllFields(context: Ctx): Promise<void> {
  try {
    const coll = context.document.body.fields as unknown as {
      load?: (s: string) => void;
      items?: Array<{ updateResult?: () => void }>;
    };
    if (!coll || typeof coll.load !== "function") {
      console.warn("[updateAllFields] body.fields API not available");
      return;
    }
    coll.load("items");
    await context.sync();
    const items = coll.items ?? [];
    let updated = 0;
    for (const f of items) {
      if (typeof f.updateResult === "function") {
        f.updateResult();
        updated++;
      }
    }
    if (updated) await context.sync();
    console.debug(`[updateAllFields] fields found=${items.length}, updateResult called=${updated}`);
  } catch (err) {
    // Best-effort — darf den Apply nie scheitern lassen; aber sichtbar machen, was schiefging.
    console.warn("[updateAllFields] failed:", err);
  }
}

/**
 * Fügt einen neuen Absatz relativ zu `anchor` ein und schreibt seinen Inhalt. Der Absatz wird mit dem
 * KLARTEXT geseedet (nicht leer), weil insertHtml("Replace") auf dem Content-Range eines frisch
 * eingefügten LEEREN Absatzes in Word no-opt; writeContent ersetzt den Seed danach durch die
 * formatierte Fassung (bei runs). Liefert den neuen Absatz zurück (für geordnetes Verketten).
 */
async function insertParagraphWithContent(
  context: Ctx,
  anchor: Para,
  location: "Before" | "After",
  content: EditContent | undefined,
  fallbackText: string,
): Promise<Para> {
  const seedText = cleanUnicode(runsToText(content?.runs) || fallbackText || "");
  const newPara = anchor.insertParagraph(seedText, location);
  await context.sync();
  if (content?.marker) await applyMarker(context, newPara, content.marker);
  await writeContent(context, newPara.getRange("Content"), content, fallbackText);
  if (content?.runs?.length) await applyFieldRuns(context, newPara, content.runs);
  if (content) await applyParagraphAttrs(context, newPara, content);
  return newPara;
}

/** Wendet einen Änderungsvorschlag an. `applyMode` steuert Tracking + Word-Kommentar. */
export async function applyChange(proposal: ChangeProposal, applyMode: ApplyMode = "with_tracked_changes"): Promise<void> {
  await Word.run(async (context) => {
    const para = await resolveParagraph(context, proposal.paragraph_id);
    const content = proposal.content;
    // Effektiver Zieltext: bevorzugt aus den strukturierten Runs, sonst der Plain-Fallback.
    const newText = proposal.new_text ?? runsToText(content?.runs);
    await withTracking(context, applyModeTracks(applyMode), async () => {
      switch (proposal.operation) {
        case "replace":
          if (content?.marker) await applyMarker(context, para, content.marker);
          if (proposal.replace_full) {
            // Ganzer Absatz in EINER Operation (ein Tracked Change, Formatierung im HTML).
            await writeContent(context, para.getRange("Content"), content, newText);
            if (content?.runs?.length) await applyFieldRuns(context, para, content.runs);
          } else {
            // Granular: nur echte Wort-Diffs als Tracked Change (Plain-Text); Marks separat abgleichen.
            await diffReplaceContent(context, para, newText);
            if (content?.runs?.length) await reconcileMarks(context, para, content.runs);
          }
          if (content) await applyParagraphAttrs(context, para, content);
          break;
        case "delete":
          para.delete();
          await context.sync();
          break;
        case "insert": {
          const location = proposal.position === "before" ? "Before" : "After";
          await insertParagraphWithContent(context, para, location, content, newText);
          break;
        }
        case "insert_block": {
          // Mehrere Absätze als GEORDNETEN Block einfügen. Erstes Element relativ zum Anker, jedes
          // weitere DIREKT hinter dem zuvor eingefügten Absatz ("After") — so bleibt die Reihenfolge
          // garantiert erhalten (kein "alle vor demselben Anker"-Umkippen wie bei N Einzel-Inserts).
          const firstLoc = proposal.position === "before" ? "Before" : "After";
          let prev = para;
          let first = true;
          // Leere Blöcke (ohne runs/Text) überspringen — das Modell hängt gern einen leeren Absatz an.
          const blocks = (proposal.blocks ?? []).filter((b) => (b.runs?.length ?? 0) > 0 && runsToText(b.runs).trim() !== "");
          for (const b of blocks) {
            prev = await insertParagraphWithContent(context, prev, first ? firstLoc : "After", b, runsToText(b.runs));
            first = false;
          }
          break;
        }
        case "format":
          // Reine Formatierungsänderung — KEIN Text-Rewrite. Marker + Absatz-Attribute aus
          // `content`, plus das Legacy-`format`-Feld (falls die KI dieses statt content nutzt).
          if (content?.marker) await applyMarker(context, para, content.marker);
          if (content) await applyParagraphAttrs(context, para, content);
          // WICHTIG: nach applyMarker (das den GANZEN Absatz inkl. Text in der Marker-Farbe färbt)
          // die Run-Marks nachziehen — so überschreiben die expliziten Text-Farben aus content.runs
          // die Marker-Farbe wieder auf dem Text (z. B. rotes Bullet, aber schwarzer Text).
          if (content?.runs?.length) await reconcileMarks(context, para, content.runs);
          if (proposal.format) await applyLegacyFormat(context, para, proposal.format);
          break;
        case "cross_ref":
          if (proposal.cross_ref) await applyCrossRef(context, para, proposal.cross_ref);
          break;
      }

      // KI-Begründung als Word-Kommentar (replace/format — der Absatz existiert noch).
      if (
        applyModeComments(applyMode) &&
        (proposal.operation === "replace" || proposal.operation === "format") &&
        proposal.comment?.trim()
      ) {
        para.getRange("Content").insertComment(proposal.comment.trim());
        await context.sync();
      }
    });

    // Nach jeder Änderung ALLE Felder aktualisieren — Verweise zeigen sofort die aktuelle Nummer.
    // Außerhalb von withTracking: der Feld-Recompute ist keine inhaltliche (zu trackende) Änderung.
    await updateAllFields(context);
  });
}

/** Wendet mehrere Vorschläge an; eine fehlgeschlagene Änderung bricht die übrigen nicht ab. */
export async function applyAllChanges(proposals: ChangeProposal[], applyMode: ApplyMode = "with_tracked_changes"): Promise<void> {
  for (const proposal of proposals) {
    try {
      await applyChange(proposal, applyMode);
    } catch (err) {
      console.warn(`applyChange failed for ${proposal.paragraph_id}:`, err);
    }
  }
}

/** Selektiert den Absatz (per uniqueLocalId, sonst Index), damit Word ihn in den Blick scrollt. */
export async function scrollToParagraph(paragraphId: string): Promise<void> {
  await Word.run(async (context) => {
    try {
      const para = await resolveParagraph(context, paragraphId);
      para.select();
      await context.sync();
    } catch {
      /* paragraph gone — nothing to scroll to */
    }
  });
}
